import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { useDarkMode } from "../hooks/useDarkMode";
import PublicPageNav from "../components/PublicPageNav";
import PublicPageFooter from "../components/PublicPageFooter";
import { GLOSSARY_TERMS } from "../data/glossaryTerms";

export default function GlossaryPage() {
  const [dm] = useDarkMode();

  useEffect(() => {
    document.title = "Travel & Tourism Industry Glossary — DMO, NDC, RevPAR, Agentic AI | TourismIntel";
    const desc = document.querySelector('meta[name="description"]');
    const original = desc?.getAttribute("content");
    desc?.setAttribute("content",
      "Plain-English definitions of travel and tourism industry terms: DMO, OTA, NDC, GDS, MICE, RevPAR, agentic AI, RAG and more — with links to related coverage.");
    const ld = {
      "@context": "https://schema.org",
      "@type": "DefinedTermSet",
      "@id": `${window.location.origin}/glossary`,
      name: "Travel & Tourism Industry Glossary",
      description: "Definitions of key travel, tourism, hospitality and travel-tech terms.",
      hasDefinedTerm: GLOSSARY_TERMS.map((t) => ({
        "@type": "DefinedTerm",
        name: t.name,
        description: t.definition,
        url: `${window.location.origin}/glossary#${t.id}`,
      })),
    };
    let el = document.getElementById("glossary-jsonld");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "glossary-jsonld";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(ld);
    return () => {
      if (original) desc?.setAttribute("content", original);
      document.getElementById("glossary-jsonld")?.remove();
    };
  }, []);

  const c = dm
    ? { bg: "bg-[#0f1718]", card: "bg-[#1a2324]", border: "border-slate-700/50", text: "text-slate-200", muted: "text-slate-500", sub: "text-slate-300", accent: "text-cyan-400" }
    : { bg: "bg-[#f5f6f7]", card: "bg-white", border: "border-[#e7e8e9]", text: "text-[#191c1d]", muted: "text-[#6b7a7d]", sub: "text-[#3b494c]", accent: "text-[#006874]" };

  return (
    <div className={`min-h-screen flex flex-col ${c.bg}`}>
      <PublicPageNav />
      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-10" data-testid="glossary-page">
        <header className="mb-8">
          <p className={`category-tag text-xs mb-3 flex items-center gap-2 ${c.accent}`}>
            <BookOpen className="w-3.5 h-3.5" /> INDUSTRY REFERENCE
          </p>
          <h1 className={`editorial-heading text-3xl sm:text-4xl font-semibold ${c.text}`} data-testid="glossary-page-title">
            Travel & tourism industry glossary
          </h1>
          <p className={`mt-3 text-sm leading-relaxed ${c.muted}`}>
            Plain-English definitions of the terms that run the travel industry — distribution,
            hotel performance, destination management and AI — each linked to related TourismIntel coverage.
          </p>
        </header>

        <nav className="mb-10 flex flex-wrap gap-2" data-testid="glossary-jump-nav" aria-label="Jump to term">
          {GLOSSARY_TERMS.map((t) => (
            <a key={t.id} href={`#${t.id}`}
              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${c.border} ${dm ? "text-slate-400 hover:text-cyan-300 hover:border-cyan-400/40" : "text-[#3b494c] hover:text-[#006874] hover:border-[#006874]/40"}`}>
              {t.name.split(" (")[0]}
            </a>
          ))}
        </nav>

        <div className="space-y-8">
          {GLOSSARY_TERMS.map((t) => (
            <section key={t.id} id={t.id} data-testid={`glossary-term-${t.id}`}
              className={`rounded-2xl border p-5 scroll-mt-24 ${c.card} ${c.border}`}>
              <h2 className={`text-base md:text-lg font-semibold ${c.text}`}>{t.name}</h2>
              <p className={`mt-2 text-sm leading-relaxed ${c.sub}`}>{t.definition}</p>
              {t.related?.length > 0 && (
                <p className={`mt-3 text-xs ${c.muted}`}>
                  Related:{" "}
                  {t.related.map((r, i) => (
                    <React.Fragment key={r.to + r.label}>
                      {i > 0 && " · "}
                      <Link to={r.to} className={`font-semibold hover:underline underline-offset-2 ${c.accent}`}>{r.label}</Link>
                    </React.Fragment>
                  ))}
                </p>
              )}
            </section>
          ))}
        </div>
      </main>
      <PublicPageFooter />
    </div>
  );
}
